import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Phone, PhoneOff, Video, User } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { resolveIncomingCallerName } from '../../lib/callParticipant';

const IncomingCallModal = ({ incomingCall, onAccept, onReject, resolveStaff }) => {
  const { t } = useLanguage();
  if (!incomingCall) return null;

  const isVideoCall = incomingCall.callType === 'video';
  // Resolve the real caller from the staff roster (strip any __session). Null-safe: an unknown caller
  // shows a labelled id ("User <uid>") — NEVER the literal "User null"/"User undefined" (see
  // ../../lib/callParticipant, shared + tested). Normal staff↔staff behavior is preserved.
  const { name: callerName, role: callerRole } = resolveIncomingCallerName({ incomingCall, resolveStaff, t });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center">
          {/* Caller Avatar/Icon */}
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="h-10 w-10 text-white" />
          </div>
          
          {/* Caller Information */}
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {callerName}{callerRole ? ` — ${callerRole}` : ''}
          </h3>
          
          <div className="mb-4">
            <Badge className={`${isVideoCall ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'} mb-2`}>
              {isVideoCall ? (
                <>
                  <Video className="h-3 w-3 mr-1" />
                  Video Call
                </>
              ) : (
                <>
                  <Phone className="h-3 w-3 mr-1" />
                  Voice Call
                </>
              )}
            </Badge>
          </div>
          
          <p className="text-gray-600 mb-6">
            {isVideoCall ? 'Incoming video call...' : 'Incoming voice call...'}
          </p>
          
          {/* Call Action Buttons */}
          <div className="flex justify-center space-x-4">
            <Button
              onClick={onReject}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-full"
            >
              <PhoneOff className="h-5 w-5 mr-2" />
              Decline
            </Button>
            
            <Button
              onClick={onAccept}
              className={`${isVideoCall 
                ? 'bg-green-500 hover:bg-green-600' 
                : 'bg-blue-500 hover:bg-blue-600'
              } text-white px-6 py-3 rounded-full`}
            >
              {isVideoCall ? (
                <>
                  <Video className="h-5 w-5 mr-2" />
                  Accept Video
                </>
              ) : (
                <>
                  <Phone className="h-5 w-5 mr-2" />
                  Accept
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomingCallModal;